import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslationService } from '@proximus/ng-common';
import { from, Observable } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { GetSimInfo } from '../store/actions/pricing-plan.actions';
import { GetProducts } from '../store/actions/products.actions';
import { ApplicationState, getProducts, getSimInfo } from '../store/reducers';
import { normalizeMsisdn } from './sim.utils';

@Injectable({
  providedIn: 'root',
})
export class SimService {
  private activationCodes$: Observable<[string, string]>;
  private esimTokens$: Observable<[string, string]>;

  constructor(
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
  ) {
    this.activationCodes$ = this.store.select(getSimInfo).pipe(
      filter((sim) => Boolean(sim?.msisdn && sim.activationCode)),
      map(({ msisdn, activationCode }) => [
        normalizeMsisdn(msisdn),
        activationCode,
      ]),
    );

    this.esimTokens$ = this.store.select(getProducts).pipe(
      mergeMap(products => from(products)),
      filter((product) => Boolean(product?.accessNumber && (product as any).esimToken)),
      map((product) => [
        normalizeMsisdn(product.accessNumber),
        (product as any).esimToken,
      ]),
    );
  }

  getActivationCode(msisdn: string): Observable<string> {
    msisdn = normalizeMsisdn(msisdn);
    this.store.dispatch(new GetSimInfo({ msisdn }));
    return this.activationCodes$.pipe(
      filter(([phoneNumber]) => phoneNumber === msisdn),
      map(([, code]) => code),
    );
  }

  getEsimToken(msisdn: string): Observable<string> {
    msisdn = normalizeMsisdn(msisdn);
    this.store.dispatch(new GetProducts());
    return this.esimTokens$.pipe(
      filter(([phoneNumber]) => phoneNumber === msisdn),
      map(([, token]) => token),
    );
  }

  navigateToEshareEsimPage(msisdn: string): void {
    msisdn = normalizeMsisdn(msisdn);
    this.getEsimToken(msisdn)
      .pipe(
        tap((token) => {
          const url = new URL(
            this.translationService.getTranslatedValueFor('eshare.esim.url'),
            location.origin,
          );
          url.searchParams.set('jwt', token);
          location.href = url.toString();
          // No need to unsubscribe as we're leaving the page anyway.
        }),
      )
      .subscribe();
  }
}
